import Seo from '@/components/Seo';
import { motion } from 'framer-motion';
import { BookOpen, Users, Sparkles, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-32 pb-24" data-testid="about-page">
      <Seo title="About the Publisher" description="Ink & Innovation Printworks is an independent publishing house dedicated to bold, original storytelling." />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mb-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <p className="overline text-gold mb-4">The Publishing House</p>
          <h1 className="text-4xl lg:text-5xl font-semibold mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
            About Ink and Innovation
          </h1>
          <p className="text-xl text-white/60 leading-relaxed">
            Founded in 2025 by Kavi Ocktiiv, Ink and Innovation was born from a fierce determination to 
            preserve what history has tried to erase. This is not just another publishing company—it is a 
            bold literary sanctuary dedicated to capturing marginalized stories, hidden histories, and untold 
            passages that mainstream narratives have long overlooked or deliberately omitted.
          </p>
        </motion.div>
      </section>

      {/* Mission Section */}
      <section className="bg-[#0a0a0a] py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/inklogo_transparent.png"
                alt="Ink & Innovation Printworks"
                className="w-full aspect-[4/3] object-contain"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
              <p className="overline text-gold">Our Mission</p>
              <h2 className="text-3xl lg:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
                We Safeguard Legacies
              </h2>
              <p className="text-white/60 leading-relaxed">
                We believe that true history does not belong only to the victors—it belongs to everyone. 
                Our mission is to ensure vital voices and forgotten truths do not fade into obsolescence. We 
                champion authors, historians, and storytellers who dare to excavate the buried, illuminate 
                the suppressed, and reintroduce the excluded.
              </p>
              <p className="text-white/60 leading-relaxed">
                From Indigenous legacies and overlooked American lineages to personal testimonies of 
                resilience, cultural inheritance, and quiet resistance—Ink and Innovation brings these 
                essential narratives into the light with dignity, excellence, and unapologetic authenticity. 
                Here, every manuscript is treated as sacred testimony.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="overline text-gold mb-4">Our Values</p>
            <h2 className="text-3xl lg:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
              What We Stand For
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: BookOpen,
                title: 'Preserve Memory',
                description: 'We resurrect memory and protect cultural inheritance for future generations.',
              },
              {
                icon: Users,
                title: 'Marginalized Voices',
                description: 'We champion historically silenced voices and the stories mainstream narratives omit.',
              },
              {
                icon: Sparkles,
                title: 'Historical Truth',
                description: 'We excavate buried histories and illuminate suppressed truths with rigor and care.',
              },
              {
                icon: Heart,
                title: 'Sacred Testimony',
                description: 'Every manuscript is treated as sacred testimony, given the platform it deserves.',
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card-surface p-8"
              >
                <value.icon size={32} className="text-gold mb-6" strokeWidth={1.5} />
                <h3 className="text-lg font-medium mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {value.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Submissions CTA */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="overline text-gold mb-4">For Writers</p>
            <h2 className="text-3xl lg:text-4xl font-semibold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Submit Your Manuscript
            </h2>
            <p className="text-white/60 mb-10 leading-relaxed">
              We're always looking for bold new voices. If you have a completed manuscript that 
              challenges conventions and tells a story only you can tell, we'd love to hear from you.
            </p>
            <Link to="/contact" className="btn-primary" data-testid="submit-manuscript-btn">
              Get in Touch
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
